/**
 * Every live share link in this group, and a way to pull one back.
 *
 * Links could be made and never seen again. `share.list` existed and nothing
 * called it, so once a link left the dialog that created it the only record was
 * in whatever chat it was pasted into — which is precisely the wrong place for
 * "who can see our photographs" to live. The counters matter for the same
 * reason: a link with four hundred views is a different object from one with
 * none, and only one of them is worth an awkward conversation.
 *
 * Revoking is immediate and total. There is no "expire tomorrow" here: somebody
 * opening this section has usually already decided.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, trpc, trpcClient } from "../../lib/trpc.js";
import { useT } from "../../lib/i18n.js";
import { Button } from "../../components/ui/Button.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { useToast } from "../../components/ui/Toast.js";
import "./SharedLinksSection.css";

export function SharedLinksSection({ groupId }: { groupId: string }) {
  const t = useT();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<string | null>(null);

  const links = useQuery(trpc.share.list.queryOptions({ groupId }));
  const listKey = trpc.share.list.queryKey({ groupId });

  const revoke = useMutation({
    mutationFn: (shareId: string) =>
      trpcClient.share.revoke.mutate({ groupId, shareId }),
    onSuccess: async () => {
      setConfirming(null);
      toast(t("Link revoked. It will not open again."));
      await queryClient.invalidateQueries({ queryKey: listKey });
      /* The gallery's shared badges are now wrong until this re-reads. */
      await queryClient.invalidateQueries({
        queryKey: trpc.share.sharedFiles.queryKey({ groupId }),
      });
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  const rows = links.data ?? [];
  const target = rows.find((row) => row.id === confirming);

  return (
    <section className="fc-settings__section" aria-labelledby="fc-shares">
      <div className="fc-settings__section-head">
        <h2 className="fc-settings__section-title" id="fc-shares">
          {t("Shared links")}
        </h2>
        <p className="fc-settings__section-hint">
          {t(
            "Anyone holding one of these can open what it points at without an account. Revoking a link closes it for everyone immediately.",
          )}
        </p>
      </div>

      {links.isError && (
        <p className="fc-settings__error" role="alert">
          {errorMessage(links.error)}
        </p>
      )}

      {links.isLoading ? (
        <p className="fc-settings__section-hint">{t("Looking…")}</p>
      ) : rows.length === 0 ? (
        <p className="fc-settings__section-hint">
          {t("Nothing in this group is shared outside it.")}
        </p>
      ) : (
        <ul className="fc-shares" role="list">
          {rows.map((link) => {
            const expired =
              link.expiresAt !== null && new Date(link.expiresAt) < new Date();

            return (
              <li className="fc-shares__row" key={link.id}>
                <div className="fc-shares__what">
                  <span className="fc-shares__title">
                    {link.scope === "folder"
                      ? (link.folderName ?? t("An album"))
                      : `${link.itemCount.toLocaleString()} ${
                          link.itemCount === 1 ? t("photo") : t("photos")
                        }`}
                  </span>

                  {/*
                    The token prefix, not the token. The full one exists only in
                    the copy the creator was shown once — which is what makes a
                    leaked link unrecoverable and this list worth having.
                  */}
                  <span className="fc-shares__token fc-tabular">
                    /s/{link.tokenPrefix}…
                  </span>

                  <span className="fc-shares__meta">
                    {link.createdByName ?? t("Someone who has left")}
                    {" · "}
                    {new Date(link.createdAt).toLocaleDateString()}
                    {link.hasPassword && ` · ${t("password")}`}
                    {link.allowOriginals && ` · ${t("full size")}`}
                    {!link.allowDownload && ` · ${t("no downloads")}`}
                    {expired
                      ? ` · ${t("expired")}`
                      : link.expiresAt
                        ? ` · ${t("until")} ${new Date(
                            link.expiresAt,
                          ).toLocaleDateString()}`
                        : ""}
                  </span>
                </div>

                <div className="fc-shares__use fc-tabular">
                  <span title={t("Views")}>
                    <EyeGlyph /> {link.viewCount.toLocaleString()}
                  </span>
                  <span title={t("Downloads")}>
                    <DownGlyph /> {link.downloadCount.toLocaleString()}
                  </span>
                </div>

                <Button
                  variant="secondary"
                  onClick={() => setConfirming(link.id)}
                >
                  {t("Revoke")}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {target && (
        <Dialog
          open
          onClose={() => setConfirming(null)}
          title={t("Revoke this link?")}
          description={t(
            "It stops working straight away, for everyone who has it. There is no way to bring the same link back — you would have to share again and send the new one.",
          )}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirming(null)}>
                {t("Keep it")}
              </Button>
              <Button
                variant="danger"
                loading={revoke.isPending}
                onClick={() => revoke.mutate(target.id)}
              >
                {t("Revoke")}
              </Button>
            </>
          }
        >
          <p className="fc-settings__section-hint">
            {target.viewCount > 0
              ? `${t("It has been opened")} ${target.viewCount.toLocaleString()} ${
                  target.viewCount === 1 ? t("time.") : t("times.")
                }`
              : t("It has never been opened.")}
          </p>
        </Dialog>
      )}
    </section>
  );
}

const glyph = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

const EyeGlyph = () => (
  <svg {...glyph}>
    <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" />
    <circle cx="12" cy="12" r="2.6" />
  </svg>
);

const DownGlyph = () => (
  <svg {...glyph}>
    <path d="M12 4v11M7.5 11l4.5 4.5 4.5-4.5M4 19.5h16" />
  </svg>
);
